import React from 'react';

import { Empty, Spin } from 'antd';
import {
  Area,
  Bar,
  Brush,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import IntlMessages from 'util/IntlMessages';

import { byPassTaskByTemplate, EXPRESSION_VALUE_TYPE } from '../../../constants';
import { buildAggregationsMapping } from './utils';

export const renderChartContent = ({ data, loading, widgetId, ref, widget }) => {
  if (!data || !data.metrics) {
    return loading ? (
      <Spin size="large" />
    ) : (
      <Empty
        className="gx-m-auto"
        description={<IntlMessages id="components.empty.description" />}
      />
    );
  }

  const { aggregations, queryParams, apiName = '' } = widget;
  const chartType =
    aggregations?.[byPassTaskByTemplate(queryParams?.groupBy)]?.displayType || 'bar';

  const chartTypes = {
    bar: ({ key, color }) => (
      <Bar dataKey={key} barSize={20} fill={color}>
        <LabelList
          formatter={value => {
            if (value <= 0) return null;
            return value;
          }}
          dataKey={key}
          position="top"
        />
      </Bar>
    ),
    line: ({ key, color }) => (
      <Line isAnimationActive={false} connectNulls dataKey={key} type="monotone" stroke={color} />
    ),
    area: ({ key, color }) => <Area dataKey={key} type="monotone" fill={color} stroke={color} />,
  };

  const aggregationsMapping = buildAggregationsMapping({
    aggregations,
    metrics: data.metrics,
    chartType,
    apiName,
  });

  return (
    <>
      {!loading && !!data.metrics?.length && (
        <ResponsiveContainer width="100%" height="100%" minHeight={0} className="cancelDrag">
          <ComposedChart data={data.metrics} ref={ref}>
            <XAxis dataKey="key" key={`${widgetId}_xaxis`} />
            <YAxis
              tickFormatter={
                widget?.queryParams?.valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
                  ? value => `${value}%`
                  : undefined
              }
              domain={
                widget?.queryParams?.valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
                  ? [0, 100]
                  : ['auto', 'auto']
              }
            />
            <Tooltip
              content={<CustomTooltip widget={widget} valueType={widget?.queryParams?.valueType} />}
            />
            <Legend
              verticalAlign="top"
              wrapperStyle={{
                overflow: 'visible',
                paddingBottom: '20px',
              }}
              iconSize={8}
            />
            <CartesianGrid stroke="#F5F5F5" />
            {aggregationsMapping.map(type => (
              <React.Fragment key={`${widgetId}_${type.aggregation}_${type.displayType}`}>
                {chartTypes[type.displayType]({ key: type.aggregation, color: type.color })}
              </React.Fragment>
            ))}
            <Brush dataKey="key" height={30} stroke="#8884D8" tick={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      {loading && <Spin size="large" />}
      {!loading && !data.metrics?.length && (
        <Empty
          className="gx-m-auto"
          description={<IntlMessages id="components.empty.description" />}
        />
      )}
    </>
  );
};

const CustomTooltip = ({ active, payload, valueType = EXPRESSION_VALUE_TYPE.NUMBER, widget }) => {
  if (active && payload && payload.length) {
    const data = payload[0];

    const formattedValue = value => {
      return valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE ? `${value}%` : value;
    };
    return (
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <p className="gx-text-warning">{data.payload.key}</p>
        {Object.entries(data.payload)
          .map(([key, _]) => (
            <p
              key={key}
              className="gx-text-primary gx-m-0"
            >{`${key}: ${formattedValue(data.payload[key])}`}</p>
          ))
          .filter(t => {
            return t.key !== 'key';
          })}
      </div>
    );
  }
  return null;
};
